import type { FC, ReactNode } from 'react'
import type { CartItem, Quote } from '@/types'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/lib/api-client'
import { useLocale } from '@/lib/i18n'

const STORAGE_KEY = 'np-cart-v1'
const MAX_QTY = 100000

export const lineKey = (productId: string, variantId: string | null): string => `${productId}:${variantId ?? ''}`

const sanitize = (input: unknown): Array<CartItem> => {
    if (!Array.isArray(input)) return []

    return input
        .filter((entry): entry is CartItem => Boolean(entry) && typeof entry.productId === 'string' && Number.isFinite(Number(entry.qty)))
        .map(entry => ({
            productId: entry.productId,
            variantId: typeof entry.variantId === 'string' && entry.variantId ? entry.variantId : null,
            qty: Math.min(MAX_QTY, Math.max(1, Math.floor(Number(entry.qty))))
        }))
        .slice(0, 40)
}

interface CartContextValue {
    items: Array<CartItem>
    hydrated: boolean
    lineCount: number
    isOpen: boolean
    open: () => void
    close: () => void
    addItem: (item: CartItem) => void
    setQty: (key: string, qty: number) => void
    removeItem: (key: string) => void
    removeKeys: (keys: Array<string>) => void
    clear: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

export const CartProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<Array<CartItem>>([])
    const [hydrated, setHydrated] = useState(false)
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY)

            if (raw) setItems(sanitize(JSON.parse(raw)))
        }
        catch {
            setItems([])
        }

        setHydrated(true)

        const onStorage = (event: StorageEvent): void => {
            if (event.key !== STORAGE_KEY) return

            try {
                setItems(sanitize(event.newValue ? JSON.parse(event.newValue) : []))
            }
            catch {
                setItems([])
            }
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    useEffect(() => {
        if (!hydrated) return

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
        }
        catch {
            /* storage can be full or blocked; the cart still works for this visit */
        }
    }, [items, hydrated])

    const addItem = useCallback((item: CartItem) => {
        setItems(current => {
            const key = lineKey(item.productId, item.variantId)
            const existing = current.find(entry => lineKey(entry.productId, entry.variantId) === key)

            if (existing) {
                return current.map(entry => lineKey(entry.productId, entry.variantId) === key
                    ? { ...entry, qty: Math.min(MAX_QTY, entry.qty + item.qty) }
                    : entry)
            }

            return [...current, { ...item, qty: Math.min(MAX_QTY, Math.max(1, item.qty)) }]
        })
    }, [])

    const setQty = useCallback((key: string, qty: number) => {
        const next = Math.floor(qty)

        setItems(current => next < 1
            ? current.filter(entry => lineKey(entry.productId, entry.variantId) !== key)
            : current.map(entry => lineKey(entry.productId, entry.variantId) === key ? { ...entry, qty: Math.min(MAX_QTY, next) } : entry))
    }, [])

    const removeItem = useCallback((key: string) => {
        setItems(current => current.filter(entry => lineKey(entry.productId, entry.variantId) !== key))
    }, [])

    const removeKeys = useCallback((keys: Array<string>) => {
        if (keys.length === 0) return

        setItems(current => current.filter(entry => !keys.includes(lineKey(entry.productId, entry.variantId))))
    }, [])

    const clear = useCallback(() => setItems([]), [])
    const open = useCallback(() => setIsOpen(true), [])
    const close = useCallback(() => setIsOpen(false), [])

    const value = useMemo<CartContextValue>(() => ({
        items,
        hydrated,
        lineCount: items.length,
        isOpen,
        open,
        close,
        addItem,
        setQty,
        removeItem,
        removeKeys,
        clear
    }), [items, hydrated, isOpen, open, close, addItem, setQty, removeItem, removeKeys, clear])

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext)

    if (!context) throw new Error('useCart must be used inside CartProvider.')

    return context
}

interface QuoteState {
    quote: Quote | null
    loading: boolean
    error: string | null
    empty: boolean
    hydrated: boolean
    refresh: () => void
}

/** Prices the current cart on the server: wholesale tiers, offers, stock and shipping. */
export const useCartQuote = (governorateId: string | null = null, enabled = true): QuoteState => {
    const { items, hydrated, removeKeys } = useCart()
    const locale = useLocale()
    const [quote, setQuote] = useState<Quote | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [nonce, setNonce] = useState(0)
    const requestId = useRef(0)
    const itemsKey = JSON.stringify(items)

    useEffect(() => {
        if (!hydrated || !enabled) return

        if (items.length === 0) {
            requestId.current += 1
            setQuote(null)
            setLoading(false)
            setError(null)

            return
        }

        requestId.current += 1

        const id = requestId.current

        setLoading(true)

        const timer = window.setTimeout(async () => {
            try {
                const data = await api<{ quote: Quote, removedKeys: Array<string> }>('/api/cart/quote', {
                    body: { items, governorateId },
                    locale
                })

                if (id !== requestId.current) return

                setQuote(data.quote)
                setError(null)

                if (data.removedKeys.length > 0) removeKeys(data.removedKeys)
            }
            catch (caught) {
                if (id === requestId.current) setError(caught instanceof Error ? caught.message : 'Error')
            }
            finally {
                if (id === requestId.current) setLoading(false)
            }
        }, 200)

        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hydrated, enabled, itemsKey, governorateId, locale, nonce])

    const refresh = useCallback(() => setNonce(value => value + 1), [])

    return { quote, loading, error, empty: hydrated && items.length === 0, hydrated, refresh }
}
